import { SubmitOrderEvent } from '@/services/analytics/events/';
import { AnalyticsEventType, Commerce, EmptyValueDescription } from '@/services/analytics/platforms/adobe-analytics/models';
import { BagPickupLocation, Order } from '@/models';
import { CountryCode } from '@/store/ui';
import moment from 'moment';
import { getGroupOrderParticipantCount } from '@/services/order/order-helper';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { ServicesContainer } from '@/services';
import { AdobeBaseEvent } from './adobe-base-event';
import { ProductListItem } from '../classes/product-list-item';
import { addLeadingZero } from '@/helpers';
import { PageParameters } from '@/services/analytics/models';
import { TargetDecisionScope } from '@/models/adobe-target';
import { ORDER_CONFIRMATION_DECISION_SCOPES } from '../constants';
import { buildTargetPageParameters } from '../helpers';

export class AdobeSubmitOrderEvent extends AdobeBaseEvent {
  event = AnalyticsEventType.SubmitOrder;
  commerce: Commerce = { ... this.commerce, purchases: { value: 1 }};
  targetPageParameters?: PageParameters;
  targetDecisionScope?: TargetDecisionScope[];

  constructor(event: SubmitOrderEvent, store: Store<AppState>, services: ServicesContainer) {
    super(store, services);
    
    if (event.order.order.meals) {
      this.productListItems = ProductListItem.getProductListItems(event.order.order.meals, store, services.order.isDelivery());
    }

    const order = event.order;
    const state = store.state;
    const payments = state.order.lastOrder.order?.order.payments;
    const paymentType =  payments?.length ? payments[0].paymentTypeName : '';
    const customer = state.customer.profile;
    const selectedRestaurant = state.restaurant.selectedRestaurant;
    const pickupTimeDetails = store.state.checkout.pickup;

    this.commerce.order = {
      purchaseID: order.order.orderId,
      orderID: order.order.orderId,
      paymentMethod: paymentType,
      currencyCode: state.ui.countryProfile.countryCode == CountryCode.US ? 'USD' : 'CAD',
      taxAmount: order.order.orderTaxAmount,
      deliveryFeeAmount: order.order.delivery?.feeAmount || 0,
      serviceFeeAmount: order.order.orderFeeAmount - (order.order.delivery?.feeAmount ?? 0),
      donationAmount: order.order.orderDonationAmount,
      priceSubTotal: order.order.orderMealsSubTotalAmount,
      priceTotal: order.order.orderTotalAmount,
      pickupTime: `${moment(order.order.pickupDateTimeLocal).format('h:mma')}|${moment(order.order.pickupDateTimeLocal).diff(moment(), 'minutes')}min`, // Formatting example: 5:00pm|45min
      priceBagTotalAmount: order.order.orderMealsUnitPrice,
      pricePromoAmount: order.order.orderDiscountAmount
    }
    
    const selectedPickupTime = pickupTimeDetails.pickupTime.value;
    if (services.order.isPickingUp() && selectedPickupTime) {
      this.commerce.sputLinkTime = moment(selectedPickupTime).format('h:mma');
      const sputLinkPosition = pickupTimeDetails.availablePickupTimes.indexOf(selectedPickupTime || '');
      if (sputLinkPosition >= 0) {
        this.commerce.sputLinkPosition = addLeadingZero(sputLinkPosition);
      }
    }

    if (order.order.discounts?.length) {
      this.commerce.order.promoCode = order.order.discounts[0].discountCode;
    }

    if (order.order.groupOrderId && order.order.meals) {
      this.commerce.order.groupOrderParticipants = getGroupOrderParticipantCount(order.order.meals) + 1; // need to add 1 since getGroupOrderParticipantCount does not include the host
    }

    if (services.order.isDelivery()) {
      this.commerce.order.driverTipAmount = order.order.orderTipAmount;
      this.commerce.driverTipCount = { value: order.order.orderTipAmount == 0.00 ? 0:1 } 
    } else if (selectedRestaurant?.crewTipPickupEnabled) { // Only add these values to the data layer if the customer saw crew tipping
      this.commerce.order.crewTipAmount =  order.order.orderCrewTipAmount;
      this.commerce.crewTipCount = { value: order.order.orderCrewTipAmount == 0.00 ? 0:1 } 
    }

    if (order.order.donations && order.order.donations.length > 0) {
      this.commerce.donationCount = { value: 1 }
    }

    if (order.discounts && order.discounts.length) {
      this.commerce.order.promoCode = order.discounts[0].promotionCode;
    }

    this.commerce.checkoutType = customer?.isGuest ? 'guest' : services.loyalty.isEnrolledInLoyalty(customer) ? 'loyalty' : 'non-loyalty';
    this.commerce.retrievalType = this.getRetrievalType(event?.order?.order);

    if (state.order.upsellMeal) {
      this.commerce.upsellOrder = { value: 1 };
    }

    this.targetPageParameters = buildTargetPageParameters(store, services, event.viewName);
    this.targetDecisionScope = ORDER_CONFIRMATION_DECISION_SCOPES;
  }

  getRetrievalType(order: Order): string {
    if(order.delivery) {
      return 'Delivery';
    }

    const bagPickupLocation = order?.bagPickupLocationId;

    // Default to 'Pickup_Inside' if order is not 'Walk_Up' nor 'Chipotlane'
    return bagPickupLocation == BagPickupLocation.WalkUpWindow ? 'Walk_Up' : 
           bagPickupLocation == BagPickupLocation.Chipotlane ? 'Chipotlane' : 'Pickup_Inside';
  }
}
